import { useEffect, useState } from "react";
import { Flag } from "lucide-react";
import Modal from "../../../components/Modal/Modal";
import Button from "../../../components/Button/Button";
import Input from "../../../components/Input/Input";
import Textarea from "../../../components/Textarea/Textarea";
import { formatDate, toApiDate } from "../utils/lessonPlanUtils";

const EMPTY_FORM = { reason: "", remarks: "", revisedDate: "" };

/** "Subject · Class · Period 4 · Thursday, 3 Jul" under the title */
const getLessonMeta = (lesson) =>
  [
    lesson?.subjectName,
    lesson?.programName,
    lesson?.period ? `Period ${lesson.period}` : "",
    formatDate(lesson?.lessonDate, "EEEE, d MMM"),
  ]
    .filter(Boolean)
    .join(" · ");

/** Flag a lesson with a reason (shown later as "Teacher Remarks"), optional remarks and a revised delivery date. */
const FlagLessonModal = ({ lesson, isSubmitting, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  useEffect(() => {
    // Re-flagging: start from what was saved before
    setForm({
      reason: lesson?.flagReason || (lesson?.isFlagged ? lesson.remark : "") || "",
      remarks: lesson?.flagRemarks || "",
      revisedDate: toApiDate(lesson?.revisedDeliveryDate),
    });
    setError("");
  }, [lesson]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "reason") setError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const reason = form.reason.trim();
    if (!reason) {
      setError("Please enter a reason for flagging");
      return;
    }
    onSubmit({ reason, remarks: form.remarks.trim(), revisedDate: form.revisedDate });
  };

  const meta = getLessonMeta(lesson);

  return (
    <Modal
      isOpen={Boolean(lesson)}
      onClose={isSubmitting ? () => {} : onClose}
      title={
        <>
          <span className="lp-modal-title">Flag Lesson</span>
          {meta && <span className="lp-modal-subtitle">{meta}</span>}
        </>
      }
      size="sm"
      closable={false}
      footer={
        <div className="lp-modal-actions">
          <Button variant="outline-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form="lp-flag-form" variant="danger" icon={<Flag size={14} />} loading={isSubmitting}>
            Submit Flag
          </Button>
        </div>
      }
    >
      <form id="lp-flag-form" onSubmit={handleSubmit} noValidate>
        <Input
          label="Reason for Flagging"
          name="reason"
          id="lp-flag-reason"
          value={form.reason}
          onChangeHandler={handleChange}
          placeholder="e.g. Session cancelled due to annual day"
          maxLength={255}
          error={error}
          required
          autoFocus
        />
        <Textarea
          label="Remarks"
          name="remarks"
          id="lp-flag-remarks"
          rows={3}
          value={form.remarks}
          onChangeHandler={handleChange}
          placeholder="Any additional details"
        />
        <Input
          label="Revised Delivery Date"
          type="date"
          name="revisedDate"
          id="lp-flag-revised-date"
          value={form.revisedDate}
          onChangeHandler={handleChange}
          // A revised date can't be before the lesson's own date
          min={toApiDate(lesson?.lessonDate) || undefined}
        />
      </form>
    </Modal>
  );
};

export default FlagLessonModal;
